// @spec AST-STR-009
'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { TagManagementCard } from '@/features/settings/assets/components/tag-management-card';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/asset-tags')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.assetTags'),
  }),
  component: AssetTagsPage,
});

/**
 * Asset tags management page.
 *
 * Allows admins to view, rename, merge, and delete tags used across assets.
 */
function AssetTagsPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { isAdmin, isLoading: isRoleLoading } = useOrgRole();

  // Redirect non-admins to dashboard
  useEffect(() => {
    if (!isRoleLoading && !isAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [isRoleLoading, isAdmin, navigate]);

  // Don't render content while checking permissions
  if (!isRoleLoading && !isAdmin) {
    return null;
  }

  // Get internal org ID from Clerk organization metadata
  const orgId = (organization?.publicMetadata as { internalId?: string } | undefined)?.internalId;

  // Show skeleton while loading org info
  if (isRoleLoading || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-4xl space-y-6">
          <header>
            <Skeleton className="h-9 w-48" />
            <Skeleton className="h-5 w-80 mt-2" />
          </header>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('assetTagsPage.title')}</h1>
          <p className="mt-1 text-muted-foreground">{t('assetTagsPage.description')}</p>
        </header>

        <TagManagementCard orgId={orgId} />
      </div>
    </div>
  );
}
